import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getUsers,
  setUserPermissions,
  onboardUser,
  deleteUser,
  type User,
  type SetPermissionsRequest,
  type OnboardUserRequest,
} from "@/services/api/users";

const usersKeys = {
  all: ["users"] as const,
  lists: () => [...usersKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...usersKeys.lists(), { filters }] as const,
  details: () => [...usersKeys.all, "detail"] as const,
  detail: (entraObjectId: string) =>
    [...usersKeys.details(), entraObjectId] as const,
};

export function useUsers(
  options?: Omit<UseQueryOptions<User[], Error>, "queryKey" | "queryFn">
) {
  return useQuery<User[], Error>({
    queryKey: usersKeys.list(),
    queryFn: getUsers,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useSetUserPermissions() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { entraObjectId: string; data: SetPermissionsRequest }
  >({
    mutationFn: ({ entraObjectId, data }) =>
      setUserPermissions(entraObjectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      toast.success("Permissions updated", {
        description: "User permissions have been successfully updated.",
      });
    },
    onError: (error) => {
      toast.error("Failed to update permissions", {
        description:
          error.message || "An error occurred while updating permissions.",
      });
    },
  });
}

export function useOnboardUser() {
  return useMutation<User, Error, OnboardUserRequest>({
    mutationFn: onboardUser,
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      toast.success("User deleted", {
        description: "The user has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast.error("Failed to delete user", {
        description:
          error.message || "An error occurred while deleting the user.",
      });
    },
  });
}

export { usersKeys };
